import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight, Laptop, Truck, MapPin, Clock, Send, Users } from "lucide-react";

import { SiteHeader } from "@/components/site/site-header";
import { SiteFooter } from "@/components/site/site-footer";
import { PublicErrorState } from "@/components/site/public-error";

import { listPrograms } from "@/lib/public.functions";
import heroImage from "@/assets/golf-cannon-activation.jpg";

const programsQuery = queryOptions({
  queryKey: ["programs", "public"],
  queryFn: () => listPrograms(),
});

const OG_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/71f6785b-9c5e-495e-85c1-9bb010364aab/id-preview-68e1c192--99ad6d6e-184b-411c-a0f1-e670d5f090a0.lovable.app-1785181678644.png";

const TITLE = "Volunteer with T1D Warriors | Burlington, Kentucky";
const DESCRIPTION =
  "T1D Warriors is a local 501(c)(3) nonprofit based in Burlington, Kentucky, serving families across Northern Kentucky and Greater Cincinnati. Volunteer remotely from anywhere or join the on-site crew at events.";

export const Route = createFileRoute("/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(programsQuery),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://volunteer.t1dw.org/" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://volunteer.t1dw.org/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "NGO",
              name: "T1D Warriors",
              url: "https://t1dw.org",
              description:
                "T1D Warriors is a local 501(c)(3) nonprofit based in Burlington, Kentucky, serving families across Northern Kentucky and Greater Cincinnati with free Type 1 diabetes programs and the T1D Warriors Scholarship Fund.",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Burlington",
                addressRegion: "KY",
                addressCountry: "US",
              },
              areaServed: [
                "Burlington, Kentucky",
                "Northern Kentucky",
                "Greater Cincinnati",
              ],
            },
            {
              "@type": "WebSite",
              name: "Warrior Volunteer",
              url: "https://volunteer.t1dw.org/",
              description:
                "The volunteer operations workspace for T1D Warriors: apply, pick up program kits, run outreach, and log activity.",
            },
          ],
        }),
      },
    ],
  }),
  errorComponent: ({ error }) => <PublicErrorState error={error} route="/" />,
  component: LandingPage,

});

const REMOTE_STEPS = [
  "Apply and tell us you want the remote track.",
  "Get approved and pick a program kit with the email already written.",
  "Add a few contacts each week and hit send. We take it from there.",
];

const ONSITE_STEPS = [
  "Apply and tell us you want to be on the crew.",
  "Get approved and we add you to the event roster for your area.",
  "Show up, run the cannon or the booth, and log your hours.",
];

function LandingPage() {
  const { data: programs } = useSuspenseQuery(programsQuery);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main style={{ paddingTop: "var(--site-hdr, 72px)" }}>
        <section className="hero-fit relative flex items-center overflow-hidden">
          <img
            src={heroImage}
            alt="T1D Warriors volunteers running a golf ball cannon activation"
            width={1920}
            height={1280}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.14_0.035_230)] via-[oklch(0.14_0.035_230)]/90 to-[oklch(0.14_0.035_230)]/40" />
          <div className="relative mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
            <p className="t-eyebrow">Burlington, Kentucky</p>
            <h1 className="t-display mt-4 max-w-4xl text-balance text-ice sm:mt-5">
              Volunteer with <span className="text-cyan">T1D Warriors</span>
            </h1>
            <p className="t-body mt-5 max-w-2xl text-ice/90 sm:mt-6">
              T1D Warriors is a local 501(c)(3) nonprofit based in Burlington, Kentucky, serving
              families living with Type 1 Diabetes across Northern Kentucky and Greater Cincinnati.
              There are two ways to help: send program outreach from your kitchen table in about an
              hour a week, or join the crew that shows up in person and runs the events.
            </p>
            <div className="mt-8 flex flex-col flex-wrap gap-5 sm:mt-10 sm:flex-row sm:items-stretch">
              <a
                href="#ways"
                className="inline-flex h-full min-h-[48px] w-full items-center justify-center gap-2 rounded-sm bg-cyan px-6 py-3 text-sm font-bold uppercase tracking-wide text-[oklch(0.14_0.035_230)] transition-opacity hover:opacity-90 sm:flex-1"
              >
                Volunteer from anywhere <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#ways"
                className="inline-flex h-full min-h-[48px] w-full items-center justify-center gap-2 rounded-sm border border-ice/40 bg-ice/5 px-6 py-3 text-sm font-bold uppercase tracking-wide text-ice transition-colors hover:border-cyan hover:text-cyan sm:flex-1"
              >
                Volunteer in person
              </a>
            </div>
          </div>
        </section>

        <div className="app-light">
          <section id="who" className="border-b border-border bg-background">
            <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 sm:py-20 md:grid-cols-2 md:gap-10 lg:px-8">
              <div>
                <p className="t-eyebrow">Who we are</p>
                <h2 className="t-h1 mt-4 text-ice">Local, not corporate</h2>
              </div>
              <div className="space-y-5">
                <p className="t-body">
                  T1D Warriors is a local 501(c)(3) nonprofit based in Burlington, Kentucky. Living
                  with Type 1 Diabetes is demanding enough without cost getting in the way. We
                  believe every family should have the tools, support, and experiences that make it
                  easier, so we make ours free and open to everyone. No cost, no qualifying, no
                  barriers, so that no family is ever left out.
                </p>
                <p className="t-body">
                  Because we are small, volunteer work here is not busywork. There is no committee
                  between you and the result. Ten emails you send this week can turn into a golf
                  outing that funds scholarships. One Saturday you give can be the reason a family
                  walks away with gear and a phone number to call.
                </p>
                <a
                  href="https://t1dw.org"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-cyan hover:underline"
                >
                  Read the full mission at t1dw.org <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </section>

          <section id="ways" className="band-soft border-b border-border">
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
              <p className="t-eyebrow">Ways to help</p>
              <h2 className="t-h1 mt-4 text-ice">Pick the one that fits your life</h2>
              <p className="t-body mt-4 max-w-2xl">
                Most people fit clearly into one of these. If you want to do both, say so and we
                will set you up for both.
              </p>

              <div className="mt-8 grid gap-6 sm:mt-10 lg:grid-cols-2 lg:gap-8">
                <TrackCard
                  icon={Laptop}
                  eyebrow="Track one"
                  name="Remote outreach"
                  pitch="We built our own volunteer app that does the work for you. You add someone you think would care about one of our programs, pick a ready-made message, and click send. The app sends it and handles the replies."
                  where="Anywhere. You never have to travel."
                  time="About an hour a week, on your schedule."
                  points={[
                    "Add someone who would care: a golf course, clinic, school, employer, or church.",
                    "Pick the ready-made message for that program.",
                    "Click send. The app sends it and keeps the replies in one place.",
                  ]}
                  outcome="Every send you make puts a T1D Warriors program in front of an organization that can host it."
                  cta="Start remote outreach"
                  track="remote"
                  featured
                />
                <TrackCard
                  icon={Truck}
                  eyebrow="Track two"
                  name="On-site crew"
                  pitch="Be the hands and the faces at the event. Load in, run the golf ball cannon, work the booth, talk to families, tear down, go home tired."
                  where="Burlington, Northern Kentucky, and Greater Cincinnati."
                  time="A few hours per event, a handful of events a year."
                  points={[
                    "Run the golf ball cannon at outings and keep the line moving.",
                    "Set up and staff the T1D Warriors booth at community events.",
                    "Hand out gear and talk with newly diagnosed families face to face.",
                  ]}
                  outcome="Events only happen when there are enough people willing to show up early and stay late."
                  cta="Join the on-site crew"
                  track="onsite"
                />
              </div>
            </div>
          </section>

          <section id="programs" className="border-b border-border bg-background">
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
              <p className="t-eyebrow">What you are bringing people</p>
              <h2 className="t-h1 mt-4 text-ice">The programs behind every ask</h2>
              <p className="t-body mt-4 max-w-2xl">
                Remote volunteers pitch these. On-site volunteers run them. Either way, this is what
                your time turns into.
              </p>
              <div className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 lg:grid-cols-3">
                {programs.map((program) => (
                  <article
                    key={program.id}
                    className="panel group relative overflow-hidden p-5 transition-colors hover:border-cyan/60 sm:p-6"
                  >
                    <span className="absolute left-0 top-0 h-full w-[3px] bg-cyan" />
                    <h3 className="t-h3 text-ice">{program.name}</h3>
                    <p className="t-body mt-3 text-sm">{program.summary}</p>
                    {program.site_url ? (
                      <a
                        href={program.site_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-5 inline-flex items-center gap-2 text-[0.7rem] font-semibold tracking-wide text-cyan hover:underline"
                      >
                        Program page <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="how" className="band-soft">
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
              <p className="t-eyebrow">How it works</p>
              <h2 className="t-h1 mt-4 text-ice">Three steps, either way</h2>
              <div className="mt-8 grid gap-5 sm:mt-10 lg:grid-cols-2">
                <StepStrip icon={Send} label="Remote outreach" steps={REMOTE_STEPS} />
                <StepStrip icon={Users} label="On-site crew" steps={ONSITE_STEPS} />
              </div>
            </div>
          </section>

          <section className="band-soft">
            <div className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 sm:pb-20 lg:px-8">
              <div className="panel flex flex-col items-start justify-between gap-6 border-cyan/50 bg-cyan/5 p-6 sm:p-10 md:flex-row md:items-center">
                <div>
                  <h2 className="t-h2 text-ice">An hour a week is enough to matter here</h2>
                  <p className="t-body mt-2 max-w-2xl">
                    Every application is read by an actual person on our team, usually within a few
                    days. Tell us which track you want and we will get you set up.
                  </p>
                </div>
                <Link
                  to="/apply"
                  className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-sm bg-cyan px-6 py-3 text-sm font-bold uppercase tracking-wide text-[oklch(0.99_0.005_230)] transition-opacity hover:opacity-90 md:w-auto"
                >
                  Start your application <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}


function TrackCard({
  icon: Icon,
  eyebrow,
  name,
  pitch,
  where,
  time,
  points,
  outcome,
  cta,
  track,
  featured,
}: {
  icon: typeof Laptop;
  eyebrow: string;
  name: string;
  pitch: string;
  where: string;
  time: string;
  points: string[];
  outcome: string;
  cta: string;
  track: "remote" | "onsite";
  featured?: boolean;
}) {
  return (
    <article
      className={`panel flex flex-col p-6 sm:p-8 ${featured ? "border-cyan/60" : ""}`}
    >
      <div className="flex items-center justify-between gap-3">
        <Icon className="h-7 w-7 shrink-0 text-cyan" />
        <span className="text-[0.65rem] tracking-wide text-muted-foreground">
          {eyebrow}
        </span>
      </div>
      <h3 className="t-h2 mt-5 text-ice sm:mt-6">{name}</h3>
      <p className="t-body mt-3">{pitch}</p>

      <dl className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="rounded-sm border border-border bg-secondary/60 p-3">
          <dt className="flex items-center gap-2 text-[0.62rem] tracking-wide text-cyan">
            <MapPin className="h-3.5 w-3.5 shrink-0" /> Where
          </dt>
          <dd className="mt-1.5 text-sm text-foreground/85">{where}</dd>
        </div>
        <div className="rounded-sm border border-border bg-secondary/60 p-3">
          <dt className="flex items-center gap-2 text-[0.62rem] tracking-wide text-cyan">
            <Clock className="h-3.5 w-3.5 shrink-0" /> Time
          </dt>
          <dd className="mt-1.5 text-sm text-foreground/85">{time}</dd>
        </div>
      </dl>

      <ul className="mt-6 space-y-3">
        {points.map((point) => (
          <li key={point} className="flex gap-3 text-sm text-foreground/85">
            <span className="mt-2 h-[3px] w-4 shrink-0 bg-cyan" />
            {point}
          </li>
        ))}
      </ul>

      <p className="t-body mt-6 mb-6 border-l-2 border-cyan pl-4 text-sm">{outcome}</p>

      <Link
        to="/apply"
        search={{ track }}
        className="mt-auto inline-flex items-center justify-center gap-2 rounded-sm bg-cyan px-6 py-3 text-sm font-bold uppercase tracking-wide text-[oklch(0.99_0.005_230)] transition-opacity hover:opacity-90"
      >
        {cta} <ArrowRight className="h-4 w-4" />
      </Link>
    </article>
  );
}


function StepStrip({
  icon: Icon,
  label,
  steps,
}: {
  icon: typeof Send;
  label: string;
  steps: string[];
}) {
  return (
    <div className="panel p-6 sm:p-8">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 shrink-0 text-cyan" />
        <p className="text-[0.68rem] font-semibold tracking-wide text-cyan">
          {label}
        </p>
      </div>
      <ol className="mt-6 space-y-5">
        {steps.map((step, index) => (
          <li key={step} className="flex gap-4">
            <span className="shrink-0 text-sm font-bold text-cyan">0{index + 1}</span>
            <span className="text-sm text-foreground/85">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );

}
